import { InvestorPitch } from '../../types';
import Card from '../ui/Card';
import Button from '../ui/Button';

interface PitchViewProps {
  pitch: InvestorPitch;
  onDownload?: () => void;
}

export default function PitchView({ pitch, onDownload }: PitchViewProps) {
  const handleDownload = () => {
    if (onDownload) {
      onDownload();
    } else {
      // Default PDF download implementation
      window.print();
    }
  };

  return (
    <div className="space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto pr-2">
      {/* Executive Summary */}
      {pitch.executiveSummary && (
        <Card className="bg-primary-50 border-primary-200">
          <h3 className="text-xl font-display font-bold text-primary-900 mb-2">
            Executive Summary
          </h3>
          <p className="text-neutral-700 leading-relaxed">{pitch.executiveSummary}</p>
        </Card>
      )}

      {/* Core Lures */}
      {pitch.lures.length > 0 && (
        <Card>
          <h3 className="text-xl font-display font-bold text-neutral-900 mb-3">
            Why Invest? (The Lures)
          </h3>
          <ul className="space-y-2">
            {pitch.lures.map((lure, index) => (
              <li key={index} className="flex items-start gap-2">
                <span className="text-primary-600 font-bold mt-1">•</span>
                <span className="text-neutral-700">{lure}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* The Opportunity */}
      {pitch.opportunity.headline && (
        <Card>
          <h3 className="text-xl font-display font-bold text-neutral-900 mb-2">
            {pitch.opportunity.headline}
          </h3>
          <p className="text-neutral-700 leading-relaxed mb-2">
            {pitch.opportunity.content}
          </p>
          {pitch.opportunity.marketSize && (
            <p className="text-sm text-neutral-600">
              <strong>Market Size:</strong> {pitch.opportunity.marketSize}
            </p>
          )}
          {pitch.opportunity.targetCustomers && (
            <p className="text-sm text-neutral-600">
              <strong>Target Customers:</strong> {pitch.opportunity.targetCustomers}
            </p>
          )}
        </Card>
      )}

      {/* The Advantage */}
      {pitch.advantage.headline && (
        <Card>
          <h3 className="text-xl font-display font-bold text-neutral-900 mb-2">
            {pitch.advantage.headline}
          </h3>
          <p className="text-neutral-700 leading-relaxed mb-2">
            {pitch.advantage.content}
          </p>
          {pitch.advantage.ip && (
            <p className="text-sm text-neutral-600">
              <strong>IP/Technology:</strong> {pitch.advantage.ip}
            </p>
          )}
          {pitch.advantage.validation && (
            <p className="text-sm text-neutral-600">
              <strong>Validation:</strong> {pitch.advantage.validation}
            </p>
          )}
        </Card>
      )}

      {/* Risk Mitigation */}
      {pitch.riskMitigation.length > 0 && (
        <Card>
          <h3 className="text-xl font-display font-bold text-neutral-900 mb-3">
            Risk Mitigation
          </h3>
          <div className="space-y-3">
            {pitch.riskMitigation.map((item, index) => (
              <div key={index} className="border-l-4 border-primary-500 pl-3">
                <p className="font-semibold text-neutral-900 mb-1">{item.risk}</p>
                <p className="text-sm text-neutral-700">{item.mitigation}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Fears & Responses */}
      {pitch.fears.length > 0 && (
        <Card>
          <h3 className="text-xl font-display font-bold text-neutral-900 mb-3">
            Addressing Investor Concerns
          </h3>
          <div className="space-y-3">
            {pitch.fears.map((item, index) => (
              <div key={index} className="border-l-4 border-accent-500 pl-3">
                <p className="font-semibold text-neutral-900 mb-1">{item.fear}</p>
                <p className="text-sm text-neutral-700">{item.response}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Go-to-Market */}
      {pitch.goToMarket.headline && (
        <Card>
          <h3 className="text-xl font-display font-bold text-neutral-900 mb-2">
            {pitch.goToMarket.headline}
          </h3>
          <p className="text-neutral-700 leading-relaxed">
            {pitch.goToMarket.content}
          </p>
        </Card>
      )}

      {/* Team */}
      {pitch.team.headline && (
        <Card>
          <h3 className="text-xl font-display font-bold text-neutral-900 mb-2">
            {pitch.team.headline}
          </h3>
          <p className="text-neutral-700 leading-relaxed">
            {pitch.team.content}
          </p>
        </Card>
      )}

      {/* Financials */}
      {pitch.financials.headline && (
        <Card>
          <h3 className="text-xl font-display font-bold text-neutral-900 mb-2">
            {pitch.financials.headline}
          </h3>
          <p className="text-neutral-700 leading-relaxed">
            {pitch.financials.content}
          </p>
        </Card>
      )}

      {/* Competition */}
      {pitch.competition.headline && (
        <Card>
          <h3 className="text-xl font-display font-bold text-neutral-900 mb-2">
            {pitch.competition.headline}
          </h3>
          <p className="text-neutral-700 leading-relaxed">
            {pitch.competition.content}
          </p>
        </Card>
      )}

      {/* Download Button */}
      <div className="sticky bottom-0 bg-white pt-4 pb-2 border-t border-neutral-200">
        <Button onClick={handleDownload} className="w-full">
          Download Pitch Deck (PDF)
        </Button>
      </div>
    </div>
  );
}

